/* Tropical Distillery theme — small, dependency-free enhancements.
   Every feature degrades to a plain HTML form or link without JS. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Age gate ---------- */
  const AGE_KEY = 'td-age-verified';
  const gate = $('[data-age-gate]');
  if (gate) {
    let verified = false;
    try { verified = localStorage.getItem(AGE_KEY) === '1'; } catch (e) { /* storage blocked */ }
    if (!verified) {
      gate.hidden = false;
      document.body.classList.add('is-locked');
      $('[data-age-confirm]', gate)?.focus();
    }
    $('[data-age-confirm]', gate)?.addEventListener('click', () => {
      try { localStorage.setItem(AGE_KEY, '1'); } catch (e) { /* storage blocked */ }
      gate.hidden = true;
      document.body.classList.remove('is-locked');
    });
  }

  /* ---------- Mobile menu ---------- */
  const menu = $('[data-mobile-menu]');
  $$('[data-menu-toggle]').forEach((btn) => btn.addEventListener('click', () => {
    const open = !menu.classList.contains('is-open');
    menu.classList.toggle('is-open', open);
    document.body.classList.toggle('is-locked', open);
    $$('[data-menu-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
  }));

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = step.parentElement.querySelector('input');
    const next = Math.max(Number(input.min || 0), Number(input.value || 1) + Number(step.dataset.qtyStep));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart drawer ---------- */
  const drawer = $('[data-cart-drawer]');
  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.classList.add('is-locked');
    $('[data-drawer-close]', drawer)?.focus();
  };
  const closeDrawer = () => {
    drawer?.classList.remove('is-open');
    document.body.classList.remove('is-locked');
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-drawer-close]')) closeDrawer();
    const trigger = e.target.closest('[data-cart-trigger]');
    if (trigger && drawer && window.theme?.cartDrawer) { e.preventDefault(); openDrawer(); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeDrawer();
    if (menu?.classList.contains('is-open')) $('[data-menu-toggle]')?.click();
  });

  const renderCart = async () => {
    const res = await fetch(`${window.theme.routes.root}?sections=cart-drawer`);
    const data = await res.json();
    const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = $('[data-cart-drawer-inner]', html);
    const current = $('[data-cart-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
    const count = Number(fresh?.dataset.count || 0);
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form || !window.theme?.cartDrawer || !drawer) return;
    e.preventDefault();
    const button = $('[type="submit"]', form);
    const label = button?.innerHTML;
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    try {
      const res = await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }
      await renderCart();
      openDrawer();
    } catch (err) {
      alert(err.message);
    } finally {
      if (button) { button.disabled = false; button.innerHTML = label; }
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    await fetch(window.theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(input.dataset.lineQty), quantity: Number(input.value) }),
    });
    if (input.closest('[data-cart-drawer]')) await renderCart();
    else window.location.reload();
  });

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (!remove) return;
    e.preventDefault();
    const input = document.querySelector(`[data-line-qty="${remove.dataset.lineRemove}"]`);
    if (input) { input.value = 0; input.dispatchEvent(new Event('change', { bubbles: true })); }
  });

  /* ---------- Product gallery + variants ---------- */
  $$('[data-product]').forEach((root) => {
    const main = $('[data-main-media]', root);
    $$('[data-thumb]', root).forEach((thumb) => thumb.addEventListener('click', () => {
      const img = $('img', main);
      img.src = thumb.dataset.src;
      img.srcset = thumb.dataset.srcset;
      img.alt = thumb.dataset.alt;
      $$('[data-thumb]', root).forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
    }));

    const select = $('[data-variant-select]', root);
    select?.addEventListener('change', () => {
      const opt = select.selectedOptions[0];
      const idInput = $('[data-variant-id]', root);
      if (idInput) idInput.value = select.value;
      $$('[data-price]').forEach((el) => { el.textContent = opt.dataset.price; });
      const buttons = $$('[data-add-button]');
      buttons.forEach((b) => {
        b.disabled = opt.dataset.available !== 'true';
        b.textContent = opt.dataset.available === 'true' ? 'Add to Cart' : 'Sold Out';
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', select.value);
      window.history.replaceState({}, '', url);
    });
  });

  /* Sticky add-to-cart bar on mobile */
  const stickyBar = $('[data-sticky-atc]');
  const mainButton = $('[data-product] [data-add-button]');
  if (stickyBar && mainButton && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(mainButton);
    $('[data-sticky-add]', stickyBar)?.addEventListener('click', () => mainButton.click());
  }

  /* ---------- Recipe filter chips ---------- */
  $$('[data-recipe-filter]').forEach((wrap) => {
    const chips = $$('[data-filter]', wrap);
    const cards = $$('[data-recipe-flavor]');
    chips.forEach((chip) => chip.addEventListener('click', () => {
      const value = chip.dataset.filter;
      chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
      cards.forEach((card) => { card.hidden = value !== 'all' && card.dataset.recipeFlavor !== value; });
    }));
  });

  /* ---------- Reels: load near the viewport, play only while visible ---------- */
  const reels = $$('[data-reel]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const loadReel = (video) => {
    const source = $('source[data-src]', video);
    if (source) { source.src = source.dataset.src; source.removeAttribute('data-src'); video.load(); }
  };
  if (reels.length && 'IntersectionObserver' in window) {
    const reelObserver = new IntersectionObserver((entries) => entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) {
        loadReel(target);
        if (!reduceMotion) target.play().catch(() => {});
      } else if (!target.paused) {
        target.pause();
      }
    }), { rootMargin: '200px 0px', threshold: 0.25 });
    reels.forEach((v) => reelObserver.observe(v));
  } else {
    reels.forEach((v) => { loadReel(v); if (!reduceMotion) v.autoplay = true; });
  }

  /* ---------- Reveal on scroll ---------- */
  const reveals = $$('.reveal');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('is-visible'));
  }
})();
